/**
 * Editable analysis component for review items
 * Phase 3 of Human-AI Collaborative Reviews implementation
 */

import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { 
  Edit, 
  Save, 
  X, 
  History,
  RotateCcw,
  Sparkles,
  User,
  ChevronDown,
  ChevronUp,
  Clock
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface EditHistoryEntry {
  editId: string;
  timestamp: string;
  userName: string;
  userId: string;
  action: 'created' | 'modified_judgment' | 'modified_explanation' | 'deleted' | 'restored';
  previousValues?: { judgment?: string; explanation?: string; };
  newValues?: { judgment?: string; explanation?: string; };
}

interface EditableAnalysisProps {
  value: string;
  originalValue?: string;
  reviewType: 'contract' | 'quantity' | 'calculation';
  claimId: string;
  itemId: string;
  isAiGenerated?: boolean;
  isModified?: boolean;
  lastModifiedBy?: string;
  lastModifiedAt?: string;
  editHistory?: EditHistoryEntry[];
  isEditing?: boolean;
  disabled?: boolean;
  onEdit: (explanation: string) => void;
  onCancel?: () => void;
}

export function EditableAnalysis({
  value,
  originalValue,
  reviewType: _reviewType,
  claimId: _claimId,
  itemId: _itemId,
  isAiGenerated = true,
  isModified = false,
  lastModifiedBy,
  lastModifiedAt,
  editHistory = [],
  isEditing = false,
  disabled = false,
  onEdit,
  onCancel,
}: EditableAnalysisProps) {
  const [isEditMode, setIsEditMode] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [showHistory, setShowHistory] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Reset edit value when value prop changes
  useEffect(() => {
    setEditValue(value);
  }, [value]);

  // Auto-focus textarea when entering edit mode
  useEffect(() => {
    if (isEditMode && textareaRef.current) {
      textareaRef.current.focus();
      // Position cursor at end
      const length = textareaRef.current.value.length;
      textareaRef.current.setSelectionRange(length, length);
    }
  }, [isEditMode]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [editValue, isEditMode]);

  const handleEdit = () => {
    if (disabled || isEditing) return;
    setIsEditMode(true);
    setEditValue(value);
  };

  const handleSave = () => {
    if (editValue.trim() !== value.trim()) {
      onEdit(editValue.trim());
    }
    setIsEditMode(false);
  };

  const handleCancel = () => {
    setEditValue(value);
    setIsEditMode(false);
    onCancel?.();
  };

  const handleRestoreOriginal = () => {
    if (originalValue && originalValue !== value) {
      onEdit(originalValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleCancel();
    } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSave();
    }
  };

  const getStateInfo = () => {
    if (isModified) {
      return {
        icon: <Edit className="h-3 w-3" />,
        color: 'text-orange-600',
        label: 'Modified',
        description: `Last modified by ${lastModifiedBy || 'user'}${lastModifiedAt ? ` on ${new Date(lastModifiedAt).toLocaleDateString()}` : ''}`
      };
    }
    if (isAiGenerated === false) {
      return {
        icon: <User className="h-3 w-3" />,
        color: 'text-blue-600', 
        label: 'Human Created',
        description: 'Created by human reviewer'
      };
    }
    return {
      icon: <Sparkles className="h-3 w-3" />,
      color: 'text-blue-500',
      label: 'AI Generated',
      description: 'Generated by AI - click to edit'
    };
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getActionLabel = (action: EditHistoryEntry['action']) => {
    switch (action) {
      case 'created': return 'Created';
      case 'modified_explanation': return 'Modified Analysis';
      case 'modified_judgment': return 'Modified Judgment';
      case 'deleted': return 'Deleted';
      case 'restored': return 'Restored';
      default: return action;
    }
  };

  const stateInfo = getStateInfo();
  const hasChanges = editValue.trim() !== value.trim();
  const canRestoreOriginal = originalValue && originalValue !== value && isModified;

  if (isEditMode) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={cn("flex items-center gap-1 text-xs", stateInfo.color)}>
              {stateInfo.icon}
              <span>Editing Analysis</span>
            </span>
            <Badge variant="outline" className="text-xs">
              {editValue.length} chars
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            {originalValue && originalValue !== value && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowOriginal(!showOriginal)}
                className="text-xs"
              >
                {showOriginal ? 'Hide' : 'Show'} Original
              </Button>
            )}
          </div>
        </div>

        {showOriginal && originalValue && (
          <div className="bg-blue-50 border border-blue-200 p-3 rounded text-sm">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-3 w-3 text-blue-600" />
              <span className="text-xs font-medium text-blue-700">Original AI Analysis:</span>
            </div>
            <p className="text-blue-800">{originalValue}</p>
          </div>
        )}

        <div className="space-y-2">
          <Textarea
            ref={textareaRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter your analysis..."
            className="min-h-[100px] resize-none"
            disabled={isEditing}
          />
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>Ctrl+Enter to save, Esc to cancel</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handleCancel}
                disabled={isEditing}
              >
                <X className="h-3 w-3 mr-1" />
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={!hasChanges || isEditing}
              >
                <Save className="h-3 w-3 mr-1" />
                Save
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={cn("flex items-center gap-1 text-xs", stateInfo.color)} title={stateInfo.description}>
            {stateInfo.icon}
            <span>{stateInfo.label}</span>
          </span>
          {editHistory.length > 0 && (
            <Badge variant="outline" className="text-xs">
              <History className="h-3 w-3 mr-1" />
              {editHistory.length} edit{editHistory.length !== 1 ? 's' : ''}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canRestoreOriginal && (
            <Button
              size="sm"
              variant="ghost"
              onClick={handleRestoreOriginal}
              disabled={disabled || isEditing}
              className="text-xs"
              title="Restore to original AI analysis"
            >
              <RotateCcw className="h-3 w-3 mr-1" />
              Restore Original
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={handleEdit}
            disabled={disabled || isEditing}
            className="text-xs"
          >
            <Edit className="h-3 w-3 mr-1" />
            Edit
          </Button>
        </div>
      </div>

      <div className="text-sm bg-gray-50 border rounded p-3">
        <p>{value}</p>
      </div>

      {editHistory.length > 0 && (
        <Collapsible open={showHistory} onOpenChange={setShowHistory}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="text-xs">
              <Clock className="h-3 w-3 mr-1" />
              Edit History ({editHistory.length})
              {showHistory ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-2 mt-2">
            <div className="border rounded-lg bg-white">
              {editHistory.map((entry, index) => (
                <div key={entry.editId} className={cn(
                  "p-3 border-b last:border-b-0",
                  index === 0 && "bg-blue-50"
                )}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {getActionLabel(entry.action)}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        by {entry.userName}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {formatTimestamp(entry.timestamp)}
                    </span>
                  </div>
                  
                  {entry.action === 'modified_explanation' && (
                    <div className="space-y-2 text-xs">
                      {entry.previousValues?.explanation && (
                        <div className="bg-red-50 border border-red-200 p-2 rounded">
                          <span className="font-medium text-red-700">Previous:</span>
                          <p className="text-red-800 mt-1">{entry.previousValues.explanation}</p>
                        </div>
                      )}
                      {entry.newValues?.explanation && (
                        <div className="bg-green-50 border border-green-200 p-2 rounded">
                          <span className="font-medium text-green-700">Changed to:</span>
                          <p className="text-green-800 mt-1">{entry.newValues.explanation}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}